/* eslint-disable jsx-a11y/anchor-is-valid */
import React, { useContext, useEffect, useState } from "react";
import { Banner, ResourceList } from "lib/components";
import { useQuery, useMutation } from "react-apollo";
import { Badge, Card, Caption, Heading, ResourceItem, Stack, Tabs, TextStyle, Thumbnail } from "@shopify/polaris";

import { baseHelper, errorHelper } from "lib/helpers";
import { PrivateContext } from "lib/context";

import { SYNC_TEMP_VENDOR_DMS_PRODUCT, SYNC_VENDOR_DMS_PRODUCT } from "app/productOld/apollo/mutations";
import GET_TEMP_DMS_PRODUCT_LIST from "app/productOld/apollo/queries/getTempDmsProductList";

import { withErrorBoundary, withFeature } from "lib/hoc";

import constant from "lib/constant/constant";
import Popover from "lib/components/popover/popover";

const List = () => {
  const { cms = {}, currentUser = {}, history } = useContext(PrivateContext);
  const queryParams = baseHelper.getQueryParams(history.location.search);

  const [selected, setSelected] = useState(parseInt(queryParams.tab, 10) || 0);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedButtonIndex, setSelectedButtonIndex] = useState(null);
  const [popoverActive, setPopoverActive] = useState({});
  const [listLoading, setListLoading] = useState(false);
  const [importLoading, setImportLoading] = useState(false);
  const [page, setPage] = useState(parseInt(queryParams.page, 10) || 1);
  const [perPage, setPerPage] = useState(parseInt(queryParams.perPage, 10) || 20);
  const [queryValue, setQueryValue] = useState(queryParams.search || "");
  const [tempData, setTempData] = useState({ count: 0, tempProductList: [] });

  const [banner, setBanner] = useState({ isOpen: false, status: "", title: "" });

  const tabsData = [
    { id: "unsynced", panelID: "unsynced", content: cms("section.label.unsynced"), value: "unsynced" },
    { id: "synced", panelID: "synced", content: cms("section.label.synced"), value: "synced" },
    { id: "all", panelID: "all", content: cms("section.label.all"), value: "all" },
  ];

  const inputData = {
    filter: tabsData[selected].value,
    search: queryValue,
    page,
    perPage,
  };

  const { loading, data, refetch } = useQuery(GET_TEMP_DMS_PRODUCT_LIST, {
    variables: { input: inputData },
    fetchPolicy: "network-only",
  });

  const [bulkProductActions] = useMutation(SYNC_TEMP_VENDOR_DMS_PRODUCT, {
    refetchQueries: [{ query: GET_TEMP_DMS_PRODUCT_LIST, variables: { input: inputData } }],
  });

  const [importVendorProduct] = useMutation(SYNC_VENDOR_DMS_PRODUCT, {
    refetchQueries: [{ query: GET_TEMP_DMS_PRODUCT_LIST, variables: { input: inputData } }],
  });

  const tempDataResponse = baseHelper.getResponseData(data, "getTempDmsProductList");

  useEffect(() => {
    if (tempDataResponse) {
      setTempData(tempDataResponse);
    }
  }, [tempDataResponse]);

  // searching changes the result set, so reset to page 1 - staying on page 3 of the
  // unfiltered list would otherwise show an empty page for a narrower match
  const handleSearchValue = (searchValue) => {
    setQueryValue(searchValue);
    setSelectedItems([]);
    setPage(1);
  };

  const handleQueryClear = () => handleSearchValue("");

  const handleTabChange = (selectedTabIndex) => {
    setSelectedItems([]);
    setSelected(selectedTabIndex);
    setPage(1);
    const search = baseHelper.setQueryParams(history.location, {
      filter: tabsData[selectedTabIndex].value,
      tab: selectedTabIndex,
    });
    history.push(`${history.location.pathname}?${search}`);
  };

  // "Import products from your store" trigger - since DMS has no OAuth connect
  // step, this replaces the connectShop flow: pulls the vendor's DMS catalog
  // on-demand instead of waiting for the 6h background cron.
  const handleImport = async () => {
    setImportLoading(true);
    try {
      const res = await importVendorProduct();
      setImportLoading(false);

      const responseData = baseHelper.getResponseData(res.data, "syncVendorDmsProduct");
      const responseError = baseHelper.getResponseError(res.data, "syncVendorDmsProduct");

      if (responseData) {
        setBanner({ isOpen: true, title: cms("message.success.request"), status: constant.SUCCESS });
        refetch();
      }
      if (responseError) {
        setBanner({ isOpen: true, title: responseError, status: constant.CRITICAL });
      }
    } catch (exception) {
      setImportLoading(false);
      setBanner({ isOpen: true, title: errorHelper.parse(exception), status: constant.CRITICAL });
    }
  };

  const handleSync = async (id = false) => {
    const formData = { ids: selectedItems, key: "approve" };

    if (id) {
      formData.ids = [id];
      setListLoading(`approve_${id}`);
    }

    try {
      const res = await bulkProductActions({ variables: { input: { ...formData } } });
      setListLoading(false);

      const responseData = baseHelper.getResponseData(res.data, "syncTempVendorDmsProduct");
      const responseError = baseHelper.getResponseError(res.data, "syncTempVendorDmsProduct");

      if (responseData) {
        setBanner({ isOpen: true, title: cms("message.success.request"), status: constant.SUCCESS });
        setSelectedItems([]);
      }
      if (responseError) {
        setBanner({ isOpen: true, title: responseError, status: constant.CRITICAL });
      }
    } catch (exception) {
      setListLoading(false);
      setBanner({ isOpen: true, title: errorHelper.parse(exception), status: constant.CRITICAL });
    }
  };

  const bulkActions = [
    {
      content: cms("section.label.syncProduct"),
      onAction: () => handleSync(),
    },
  ];

  return (
    <>
      {banner.isOpen && (
        <>
          <Banner isOpen={banner.isOpen} status={banner.status} title={banner.title} onDismiss={() => setBanner({ isOpen: false })} />
          <br />
        </>
      )}

      <Banner
        isOpen
        status={constant.INFO}
        title={cms("message.success.importInstruction")}
        action={{ content: cms("section.label.importNow"), onAction: handleImport, loading: importLoading }}
      />
      <br />

      <Card>
        <Tabs tabs={tabsData} selected={selected} onSelect={handleTabChange} />
        <ResourceList
          resourceName={{ singular: cms("section.label.product"), plural: cms("section.label.productPlural") }}
          items={tempData.tempProductList}
          idForItem={(item) => item._id}
          selectable
          bulkActions={bulkActions}
          selectedItems={selectedItems}
          onSelectionChange={setSelectedItems}
          loading={loading}
          onQueryChange={handleSearchValue}
          onQueryClear={handleQueryClear}
          queryValue={queryValue}
          count={tempData.count}
          page={page}
          perPage={perPage}
          setPage={setPage}
          setPerPage={setPerPage}
          renderItem={(item) => {
            const { _id, image, isSynced, isVendorSync, title, barcode, updatedAt, variantCount = 1 } = item;
            // imageHelper.resize assumes every URL is hosted on this platform's own
            // ImageKit/Cloudinary CDNs and mangles anything else (splits on "/upload/",
            // which a DMSMatrix-hosted URL like api.dmsdrive.io/... doesn't have) -
            // these are already-hosted external URLs, use them as-is.
            const thumbnailImage = image || constant.NOIMAGESNAP;

            const syncedStatusType = isSynced ? constant.SYNCED : isVendorSync ? constant.PENDING : constant.NOT_SYNCED;
            const syncedStatus = isSynced
              ? cms("section.label.synced")
              : isVendorSync
              ? cms("section.label.pending")
              : cms("section.label.unsynced");

            return (
              <ResourceItem id={_id}>
                <Stack wrap={false}>
                  <Stack.Item>
                    <Thumbnail size={constant.LARGE} source={thumbnailImage} alt={`${title} image`} />
                  </Stack.Item>
                  <Stack.Item fill>
                    <Heading element="h2">
                      <div className="content-text-wrap">
                        <TextStyle variation="strong">{title}</TextStyle>
                      </div>
                    </Heading>
                    <div className="content-text-wrap">
                      <Caption>
                        {`${cms("section.label.by")} `}
                        <b>{currentUser.brandName}</b>
                        {barcode ? ` · ${barcode}` : ""}
                        {/* one listing row per product - say so when it covers several
                            DMS records, so the count reconciles with the catalog */}
                        {variantCount > 1 ? ` · ${variantCount} ${cms("section.label.variants")}` : ""}
                        {` ${cms("section.label.on")} ${baseHelper.formatDate(updatedAt)}`}
                      </Caption>
                    </div>
                    <div style={{ marginTop: "0.5em" }}>
                      <Badge status={baseHelper.getBadgeType(syncedStatusType)}>{baseHelper.ucFirst(syncedStatus)}</Badge>
                    </div>
                  </Stack.Item>
                  <Stack.Item>
                    <Popover
                      active={popoverActive[_id]}
                      setActive={() => setPopoverActive({ [_id]: !popoverActive[_id] })}
                      disabled={isVendorSync}
                      loading={_id === selectedButtonIndex && listLoading}
                      options={[
                        {
                          content: cms("section.label.sync"),
                          onAction: () => {
                            setSelectedButtonIndex(_id);
                            handleSync(_id);
                          },
                        },
                      ]}
                    />
                  </Stack.Item>
                </Stack>
              </ResourceItem>
            );
          }}
        />
      </Card>
    </>
  );
};

export default withFeature(withErrorBoundary(List), { feature: constant.DMS_PRODUCT });
